/**
 * Percent box of snippets/card-prospekt-tile.liquid (Saisoneröffnung Prospekt
 * page, 2026-10). The brochure sets its green "45%" box on the right edge of
 * the cutout, a third of the way down the product. The shop's photos carry
 * white around the product inside the file, so a box on the edge of the image
 * box floats away from a narrow ski. This script reads the smallest srcset
 * candidate of every tile image into a 40px canvas, finds the product's
 * bounding box (anything that is neither transparent nor white), and hands
 * the edge to the CSS as `--prospekt-cut-left` and `--prospekt-cut-top` on
 * the tile's media box. Without the script, or when the CDN image cannot be
 * read, the box stays where the stylesheet puts it. Nothing else is fetched.
 * The cover's slots change their shape with the viewport, so the boxes are
 * kept and placed again after a resize.
 */
(() => {
  const SAMPLE_WIDTH = 40;
  const WHITE = 236;
  const ALPHA = 30;
  const placed = new Map();

  function smallestCandidate(image) {
    const srcset = image.getAttribute('srcset') || '';
    const candidates = srcset
      .split(',')
      .map((entry) => entry.trim().split(/\s+/))
      .filter((parts) => parts[0])
      .map(([url, descriptor]) => ({ url, width: parseInt(descriptor, 10) || Infinity }))
      .sort((a, b) => a.width - b.width);
    return candidates.length ? candidates[0].url : image.currentSrc || image.src;
  }

  function boundingBox(sample) {
    const canvas = document.createElement('canvas');
    const width = SAMPLE_WIDTH;
    const height = Math.max(1, Math.round((width * sample.naturalHeight) / sample.naturalWidth));
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(sample, 0, 0, width, height);
    let data;
    try {
      data = context.getImageData(0, 0, width, height).data;
    } catch (error) {
      return null;
    }
    let minX = width;
    let maxX = -1;
    let minY = height;
    let maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const i = (y * width + x) * 4;
        if (data[i + 3] < ALPHA) continue;
        if (data[i] >= WHITE && data[i + 1] >= WHITE && data[i + 2] >= WHITE) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return null;
    return {
      left: minX / width,
      right: (maxX + 1) / width,
      top: minY / height,
      bottom: (maxY + 1) / height,
      aspect: sample.naturalWidth / sample.naturalHeight,
    };
  }

  function place(media, image, box) {
    const percent = media.querySelector('.prospekt-tile__percent');
    const frame = image.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();
    if (!percent || frame.width === 0 || mediaRect.width === 0) return;

    // object-fit: contain inside the image box; the painted area is centred.
    const frameAspect = frame.width / frame.height;
    let paintedWidth = frame.width;
    let paintedHeight = frame.height;
    if (box.aspect > frameAspect) {
      paintedHeight = frame.width / box.aspect;
    } else {
      paintedWidth = frame.height * box.aspect;
    }
    const paintedLeft = frame.left - mediaRect.left + (frame.width - paintedWidth) / 2;
    const paintedTop = frame.top - mediaRect.top + (frame.height - paintedHeight) / 2;

    const edgeX = paintedLeft + box.right * paintedWidth;
    const productTop = paintedTop + box.top * paintedHeight;
    const productHeight = (box.bottom - box.top) * paintedHeight;

    const boxRect = percent.getBoundingClientRect();
    // The box overlaps the edge by less than half of its width and never
    // leaves the media box to the right.
    let left = edgeX - boxRect.width * 0.4;
    left = Math.min(left, mediaRect.width - boxRect.width);
    left = Math.max(left, 0);
    const top = productTop + productHeight * 0.3;

    // Fractions of the media box: its aspect is fixed by the stylesheet, so
    // they hold at every viewport width.
    media.style.setProperty('--prospekt-cut-left', `${((left / mediaRect.width) * 100).toFixed(2)}%`);
    media.style.setProperty('--prospekt-cut-top', `${((top / mediaRect.height) * 100).toFixed(2)}%`);
    media.classList.add('prospekt-tile__media--cut');
  }

  function analyse(media, image) {
    const sample = new Image();
    sample.crossOrigin = 'anonymous';
    sample.decoding = 'async';
    sample.onload = () => {
      const box = boundingBox(sample);
      if (!box) return;
      placed.set(media, { image, box });
      place(media, image, box);
    };
    sample.src = smallestCandidate(image);
  }

  function prepare(media) {
    const image = media.querySelector('.prospekt-tile__image');
    if (!image || !media.querySelector('.prospekt-tile__percent')) return;
    if (image.complete && image.naturalWidth > 0) {
      analyse(media, image);
    } else {
      image.addEventListener('load', () => analyse(media, image), { once: true });
    }
  }

  let resizeTimer = 0;
  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      placed.forEach(({ image, box }, media) => place(media, image, box));
    }, 150);
  }

  function start() {
    if (!('HTMLCanvasElement' in window)) return;
    document.querySelectorAll('.prospekt-tile__media').forEach(prepare);
    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
